import React, { MouseEventHandler, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, tableClasses, filterClasses, paginationClasses, skeletonClasses } from "../common/tailwindClassnames";
import { CopyToClipboard, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import SearchChip from "../common/SearchChip";
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import SelectInput from "../common/SelectInput";
import { toast } from 'react-toastify';

type DestinationsListProps = {
    canAddDestination: boolean,
    reloadDestinations: boolean,
    destinations: Destination[],
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    destinationUrls: string[],
    loadingDestinationUrls: boolean,
    organizations: Organization[],
    loadingOrganizations: boolean,
    onAddDestination: MouseEventHandler,
    onViewDetails: Function,
    onChangeGlobalSearch: (value: string) => void,
    globalSearchStr: string,
    onChangeEndpoint: Function,
    endpointFilterValue: any,
    onChangeType: Function,
    typeFilterValue: any,
    onChangeOwner: Function,
    ownerFilterValue: any,
    requestSort: Function,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number
}

const DestinationsList = ({
    canAddDestination,
    reloadDestinations,
    destinations,
    destinationTypes,
    loadingDestinationTypes,
    destinationUrls,
    loadingDestinationUrls,
    organizations,
    loadingOrganizations,
    onAddDestination,
    onViewDetails,
    onChangeGlobalSearch,
    globalSearchStr,
    onChangeEndpoint,
    endpointFilterValue,
    onChangeType,
    typeFilterValue,
    onChangeOwner,
    ownerFilterValue,
    requestSort,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset
}: DestinationsListProps) => {
    const { t } = useTranslation();
    const [sortDirection, setSortDirection] = useState(false);
    const [sortByColumn, setSortByColumn] = useState("name");

    const hasActiveFilters = !!(globalSearchStr || endpointFilterValue || typeFilterValue || ownerFilterValue);

    const clearAllFilters = () => {
        onChangeGlobalSearch('');
        onChangeEndpoint(null);
        onChangeType(null);
        onChangeOwner(null);
    };

    return (
        <div className={containerClasses.page}>
            <div className="mb-12 flex justify-between max-w-list-table mx-auto">
                <div>
                    <h1 data-testid="destinations_page_header" className={textClasses.heading}>
                        {t("Destinations")}
                    </h1>
                </div>
                {canAddDestination && <button className={buttonClasses.primary}
                    onClick={onAddDestination}
                >
                    {t("Create Destination")}
                </button>}
            </div>
            <div className="overflow-x-auto max-w-list-table mx-auto"><div className="min-w-max">
            {/* Search row */}
            <div className="flex flex-row items-end gap-3 mb-3">
                <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} placeholder={t("Search")} showClearButton={false} clearOnSearch={true} />
                {globalSearchStr && <SearchChip value={globalSearchStr} onClear={() => onChangeGlobalSearch('')} />}
                {hasActiveFilters && (
                    <button className={`${buttonClasses.resetFilter} h-[38px] mb-2 ml-auto`} onClick={clearAllFilters}>
                        {t("Clear All Filters")}
                    </button>
                )}
            </div>
            {/* Filter dropdowns row */}
            <div className="flex flex-row gap-3 mb-3 flex-nowrap overflow-y-visible">
                {/* URL */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("URL")}</span>
                    <SelectInput
                        name="base_url__in"
                        options={destinationUrls.map(url => {
                            let hostname = url;
                            try { hostname = new URL(url).hostname; } catch {}
                            return { label: hostname, value: url };
                        })}
                        value={endpointFilterValue}
                        onChange={onChangeEndpoint}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Technology */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Technology")}</span>
                    <SelectInput
                        name="type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.id }))}
                        value={typeFilterValue}
                        onChange={onChangeType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
            </div>
            {/* Table */}
            <div className="flex flex-col w-full">
                <table className={tableClasses.container}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={tableClasses.headerCell}>
                                {t("Destination")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("name", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("name")
                                }}>
                                    {sortByColumn === "name" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("URL")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("endpoint", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("endpoint")
                                }}>
                                    {sortByColumn === "endpoint" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Technology")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("type__name", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("type__name")
                                }}>
                                    {sortByColumn === "type__name" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Workspace")}
                                <span className={tableClasses.sortIcon} onClick={() => {
                                    requestSort("owner__name", sortDirection)
                                    setSortDirection(!sortDirection)
                                    setSortByColumn("owner__name")
                                }}>
                                    {sortByColumn === "owner__name" ?
                                        sortDirection ? <SortedUp /> : <SortedDown />
                                        : <SortingArrows />}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    {(!pageLoaded || reloadDestinations) ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className={tableClasses.row} data-testid="loading">
                                    <td className={tableClasses.cell}><div className={`h-4 w-40 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-24 ${skeletonClasses.base}`} /></td>
                                </tr>
                            ))}
                        </tbody>
                        :
                        <tbody className="text-sm cursor-pointer">
                            {destinations.map((destination: Destination) => {
                                let dest_url: string | URL;
                                try {
                                    dest_url = new URL(destination.base_url).hostname
                                } catch (err) {
                                    dest_url = ""
                                }
                                return (
                                    <tr key={destination.id}
                                        className={tableClasses.row}
                                        onClick={(e) => e.metaKey || e.ctrlKey ? window.open(`/destinations/${destination.id}/configuration`, '_blank') : onViewDetails(destination.id)}
                                    >
                                        <td className={tableClasses.cell} style={{ width: "350px", maxWidth: "350px" }}>
                                            <span className="block truncate" title={destination.name}>{destination.name}</span>
                                        </td>
                                        <td className={`${tableClasses.cell} text-blue-basic`}>
                                            <div className="flex items-center gap-2">
                                                <span>{dest_url}</span>
                                                {destination.base_url && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(destination.base_url);
                                                            toast.info(`${t("URL")} ${t("copied to clipboard")}`, {
                                                                position: toast.POSITION.TOP_RIGHT,
                                                                autoClose: 2000
                                                            });
                                                        }}
                                                        className="flex items-center justify-center opacity-50 hover:opacity-100 transition-opacity"
                                                        aria-label="Copy URL to clipboard"
                                                    >
                                                        <CopyToClipboard />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className={tableClasses.cell}>
                                            {destination.type.name}
                                        </td>
                                        <td className={tableClasses.cell}>
                                            {destination.owner.name}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>}
                        {!pageLoaded || reloadDestinations ? null : destinations.length === 0 &&
                            <tbody><tr><td colSpan={4} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                />
                            </td></tr></tbody>}
                </table>
                {totalCount > page_size && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
            </div></div>
        </div>
    )
};

export default DestinationsList;
